import React from "react";
import MarketingLayout from "./MarketingLayout";
import { useSeo } from "@/lib/seo";

const H2 = ({ children }) => (
  <h2 className="font-display-italic text-3xl md:text-4xl leading-tight tracking-tight mt-12 mb-4">
    {children}
  </h2>
);
const P = ({ children }) => (
  <p className="text-[17px] text-[color:var(--ink-2)] leading-snug mb-4 max-w-2xl">
    {children}
  </p>
);

export default function Terms() {
  useSeo({
    title: "Terms – FamFeel Family Calendar",
    description:
      "FamFeel's terms of service. Plans, billing, auto-renewal, cancellation, and account deletion — kept short and honest.",
    path: "/terms",
    jsonLdId: "ld-terms",
    jsonLd: {
      "@context": "https://schema.org",
      "@type": "BreadcrumbList",
      "itemListElement": [
        { "@type": "ListItem", "position": 1, "name": "Home", "item": typeof window !== "undefined" ? window.location.origin + "/" : undefined },
        { "@type": "ListItem", "position": 2, "name": "Terms", "item": typeof window !== "undefined" ? window.location.origin + "/terms" : undefined },
      ],
    },
  });
  return (
    <MarketingLayout>
      <section className="px-6 md:px-10 pt-10 md:pt-16 pb-20 max-w-4xl">
        <div className="label-tiny mb-4">Terms of Service</div>
        <h1
          className="font-display-italic text-6xl md:text-8xl leading-[0.98] tracking-tight mb-6"
          data-testid="terms-h1"
        >
          The honest
          <br />
          fine print.
        </h1>
        <p className="text-[color:var(--ink-3)] label-tiny mb-10">
          Last updated: 1 May 2026
        </p>

        <H2>What FamFeel is</H2>
        <P>
          FamFeel is a lightweight family calendar app intended for personal
          family use. The family calendar is the core — shared events,
          birthdays, and reminders for every device. Optional mood check-ins
          and a pets log sit alongside it for families who want them. By
          creating or joining a family, you agree to use the service
          responsibly and not for any commercial, harmful, or illegal purposes.
        </P>

        <H2>Your responsibilities</H2>
        <P>
          You are responsible for keeping your family PIN private, adding
          only people who should have access to your family workspace,
          and for the content you log (notes, names, custom plans,
          calendar events, photos uploaded as avatars). Don't upload
          content that is unlawful, harassing, or infringes someone
          else's rights.
        </P>
        <P>
          If you generate a <strong>share link</strong> (such as a
          read-only grandparent calendar, a shared shopping list, or an
          iCal feed for an external calendar app), the link itself is
          the credential — anyone with the URL can read it until you
          revoke it. Treat share links and iCal feed URLs the way you'd
          treat a fridge magnet with a key on it: don't post them
          publicly, and rotate them in Settings if a phone gets lost.
        </P>

        <H2>Our responsibilities</H2>
        <P>
          We try hard to keep FamFeel running, secure, and private. That
          said, the service is provided as-is. We can't guarantee zero
          downtime, and we reserve the right to pause or modify features
          to protect other users, respond to abuse, or keep the service
          healthy.
        </P>
        <P>
          <strong>Push notifications are best-effort.</strong>{" "}
          Reminders (event nudges, the morning digest, walk reminders,
          vet/flea/tick reminders, food and water reminders, birthday
          pings) are scheduled by FamFeel but delivered through your
          browser, your operating system, and the public web-push
          infrastructure. Battery saver, Do Not Disturb, focus modes,
          offline devices, expired subscriptions, browser updates, and
          OS-level throttling can each delay or drop a notification.
          Treat any FamFeel push as a <em>helpful prompt</em>, not a
          guaranteed alert. If something genuinely matters (a vet
          appointment, a school event, an emergency), the calendar in
          the app is the source of truth — open it, don't wait for the
          ping.
        </P>

        <H2>Reminders, notifications &amp; logged information</H2>
        <P>
          FamFeel may provide reminders, notifications, and alerts as
          a convenience feature of the service. You acknowledge and
          agree that these features are provided on an "as is" and
          "as available" basis and may fail, be delayed, be
          inaccurate, or not be delivered at all.
        </P>
        <P>
          Such failures may occur for any reason, including but not
          limited to software bugs, system errors, outages, device
          issues, operating system restrictions, background app
          limitations, network connectivity problems, third-party
          service failures (including push notification providers),
          incorrect user input, time zone or scheduling errors, or
          other technical or non-technical causes.
        </P>
        <P>
          FamFeel does not guarantee that any reminder or
          notification will be sent, received, or seen at a
          particular time, or at all, nor that any such reminder or
          notification will be accurate or complete.
        </P>
        <P>
          To the fullest extent permitted by applicable law, FamFeel
          disclaims all liability for any loss, harm, damage, or
          consequences arising from or related to missed, delayed,
          inaccurate, or undelivered reminders or notifications,
          including any actions taken or not taken in reliance on
          them.
        </P>
        <P>
          You remain solely responsible for independently managing
          and attending to all appointments, obligations, and
          time-sensitive matters, including but not limited to those
          relating to health, safety, and animal care.
        </P>

        <H2>Acceptable use</H2>
        <P>
          Automated bulk access (scraping, bots, load testing) is not
          allowed. We rate-limit requests and reserve the right to
          block abusive IPs or families. Attempting to access another
          family's data — including by guessing share-link tokens or
          iCal feed URLs — is prohibited. Reselling, repackaging or
          using FamFeel as the back-end for a different product is
          prohibited.
        </P>

        <H2>Account removal &amp; your data</H2>
        <P>
          You can remove a family member anytime from within the app,
          or delete your whole family in Settings → Family. Deleting a
          member removes their check-ins, calendar tags and care logs.
          Deleting a family removes the entire workspace — events,
          check-ins, lists, share links, push subscriptions and
          billing history. We may remove content or suspend a family
          workspace that violates these terms.
        </P>
        <P>
          You can request a copy of your family's data, or its complete
          deletion outside the app, by emailing us. We aim to respond
          within 30 days. If you cancel a paid plan and don't delete
          your family, your workspace stays at the free tier with a
          reduced member cap; nothing is silently deleted.
        </P>

        <H2>Plans, billing &amp; auto-renewal</H2>
        <P>
          FamFeel has a <strong>Free</strong> tier (up to 4 members,
          calendar holds up to 50 events scheduled in the next 90 days),
          <strong>Plus</strong>{" "}
          ($3.99/month or $39.99/year, up to 6 members),{" "}
          <strong>Grand</strong> ($6.99/month or $66.99/year, up to
          10 members + Houses), and <strong>Extended Family</strong>{" "}
          ($9.99/month or $95.99/year, up to 20 members + Houses).
          Yearly plans save ~17–20% vs paying monthly. Pets count as
          members on every tier. Prices are in USD; Stripe
          auto-converts to your local currency at checkout based on
          your country.
        </P>
        <P>
          New families on Plus and Grand each get a one-time{" "}
          <strong>14-day free trial</strong> on first checkout for that
          tier. You can cancel during the trial without being charged.
          Outside the trial, paid plans{" "}
          <strong>auto-renew</strong> at the start of each new period
          (monthly or yearly) at the then-current price for your tier,
          using the payment method on file. You can cancel anytime
          from Settings → Family → Manage billing; cancellation stops
          the next renewal but does not issue a pro-rata refund for
          the current period — your plan stays active until the end of
          the paid period and then drops to the free tier.
        </P>
        <P>
          Upgrading mid-period extends your expiry by the new period
          length. Prices may change — existing subscribers will be
          notified before their next renewal and given a chance to
          cancel before the new price applies. Some pet-care features
          (vet, tick/flea, food and water reminders) are only sent to
          paid families; the underlying logs themselves stay visible
          on every tier.
        </P>
        <P>
          Payments are processed by <strong>Stripe</strong>; FamFeel
          never sees your card number. Stripe handles fraud detection,
          3-D Secure, and refunds. Subscription disputes raised
          through your card issuer (chargebacks) may result in your
          family workspace being suspended pending resolution.
        </P>

        <H2>Houses and multi-household privacy</H2>
        <P>
          The <strong>Extended Family</strong> plan offers{" "}
          <strong>Houses</strong> — a way to cluster family members under
          labels like "Mum's", "Dad's" or "Grandparents". Houses is designed
          for <strong>amicable</strong> blended, co-parenting and
          three-generation households. It groups members under those
          labels, surfaces each member's check-ins (including children's)
          to the other houses subject to that member's own per-field
          privacy toggles, and — if the house has its own{" "}
          <strong>House PIN</strong> set — requires that PIN on this
          device before anyone can see or change its members' check-ins,
          events or profiles.
        </P>
        <P>
          Anyone with just the family PIN can still: see who lives in
          each house (names + avatars), see the family-wide calendar and
          the members list, create new members (but not into a locked
          house without its PIN), rename or request deletion of a locked
          house (the residents inside see a banner and decide), and
          rename or delete an unlocked house outright. They can also{" "}
          <strong>delete unassigned members</strong> (members with no
          house) — which is deliberate so that a former household can
          still clean up when nobody's protecting those profiles, but
          also means you should only share the family PIN with people
          you'd trust to do that. Deleting a member who lives inside a
          locked house still needs the house PIN.
        </P>
        <P>
          Moving an adult or teen between houses requires{" "}
          <strong>their own personal PIN</strong> (self-consent). Small
          kids and pets can still be moved by parents without a member
          PIN — that part is trust-based. Either way, you can't move
          someone into or out of a house whose House PIN you don't know.
        </P>
        <P>
          FamFeel is a user-generated content service. All information
          within the app, including moods, notes, and statuses, is
          created and entered solely by users and is not reviewed,
          verified, monitored, or endorsed by FamFeel.
        </P>
        <P>
          To the fullest extent permitted by applicable law, FamFeel
          disclaims all liability for any loss, harm, damage, or
          consequences arising out of or related to the use of, reliance
          on, interpretation of, or actions taken (or not taken) based
          on any content available in the app. This applies regardless
          of who accesses the information, including partners, former
          partners, family members, or any person with access to a
          family PIN.
        </P>
        <P>
          Content in FamFeel does not constitute professional advice,
          assessment, diagnosis, or crisis monitoring. FamFeel does not
          provide medical, psychological, legal, or emergency services,
          and does not assume any duty of care, supervision, or
          safeguarding obligation toward users.
        </P>
        <P>
          Users are solely responsible for how they interpret and
          respond to any information within the app. FamFeel has no
          responsibility or liability for user decisions, communications,
          or actions taken on or off the platform.
        </P>
        <P>
          If any content raises concern, users should address the
          matter outside the app by contacting the relevant person
          directly and/or seeking appropriate professional or
          emergency assistance.
        </P>
        <P>
          FamFeel is <strong>not suitable for adversarial</strong> or
          legally contested co-parenting. Houses is a privacy
          convenience, not a legal custody mechanism. If you're worried
          someone is misusing the family PIN, rotate it in Settings →
          Family. FamFeel is not liable for disputes arising from shared
          access to a family workspace.
        </P>

        <H2>Not a medical, veterinary, or emergency service</H2>
        <P>
          FamFeel is a general-purpose, user-directed logging and
          communication tool intended for personal and household use
          only. It is not, and must not be used as, a medical, mental
          health, veterinary, safety, monitoring, or emergency service.
        </P>
        <P>
          All features — including but not limited to mood tracking,
          sleep, statuses, private notes, check-in history, pet-related
          logs (such as walks, feeding, veterinary visits, treatments,
          hygiene), calendar events, chores, reminders, and
          notifications — are provided solely as convenience tools for
          recording user-entered information. All data consists of
          timestamps and user-generated text or selections and is not
          reviewed, validated, monitored, or interpreted by FamFeel.
        </P>
        <P>
          To the fullest extent permitted by applicable law, FamFeel
          makes no representations or warranties of any kind, express
          or implied, regarding the accuracy, completeness, reliability,
          or usefulness of any content in the app, and expressly
          disclaims any duty to monitor, assess, or act upon such
          information.
        </P>
        <P>
          Nothing in FamFeel constitutes or should be relied upon as
          medical, psychological, veterinary, legal, or other
          professional advice, diagnosis, treatment, or supervision.
          FamFeel does not provide crisis response, safeguarding, or
          emergency support, and assumes no duty of care toward users
          or third parties.
        </P>
        <P>
          Use of the app is at your own risk. Users are solely
          responsible for how they use, interpret, and act upon any
          information recorded in or accessed through FamFeel.
        </P>
        <P>
          If you or someone in your family is in crisis, please contact
          local emergency services or a qualified professional. If a pet
          appears unwell, contact a vet — don't rely on the FamFeel
          countdown to tell you something's wrong. A reminder for the
          annual vet exam is a prompt, not medical supervision.
        </P>
        <P>
          FamFeel does not monitor, review, or act upon user activity.
          Content such as moods, notes, statuses, and check-ins is not
          read, evaluated, or escalated by FamFeel, and there is no
          human oversight of such entries. FamFeel does not and will
          not contact any third party on a user's behalf, including
          but not limited to healthcare providers, schools, social
          services, family members, or emergency services.
        </P>
        <P>
          No content or activity within the app — including low mood
          scores, concerning notes or statuses, or gaps in check-ins —
          triggers any form of intervention, alert, or response by
          FamFeel. Push notifications are limited to user-configured
          or system-generated reminders and are not monitored,
          supervised, or connected to any alert or emergency response
          system.
        </P>
        <P>
          To the fullest extent permitted by applicable law, FamFeel
          disclaims all responsibility and liability for any outcomes
          arising from or related to the use of the app, including but
          not limited to any physical, mental, medical, financial,
          relational, or other consequences resulting from content
          that is created, viewed, ignored, or acted upon by users.
        </P>
        <P>
          Users are solely responsible for how they interpret and
          respond to any information within the app. If any content
          raises concern, it is the user's responsibility to address
          the matter outside the app, including by contacting the
          relevant individual directly and/or seeking appropriate
          professional or emergency assistance.
        </P>

        <H2>No responsibility for what you do with FamFeel</H2>
        <P>
          FamFeel is provided <strong>as-is and as-available</strong>, with no
          warranties of any kind, express or implied, including but not
          limited to fitness for a particular purpose, uninterrupted
          availability, accuracy of any logged data, timely delivery of
          push notifications, or the absence of bugs.
        </P>
        <P>
          To the fullest extent permitted by law, neither FamFeel, its
          developers, nor any third-party service it depends on (Stripe,
          Cloudflare, hosting providers, web-push providers, etc.)
          accept responsibility for: (a) missed or late push
          notifications of any kind (morning nudges, birthdays, family
          nudges, vet-exam reminders, overdue walks, overdue
          treatments, food/water reminders); (b) decisions made based
          on anything displayed inside the app — including a pet's
          care-log countdown, a mood card's color, or a member's
          check-in status; (c) loss of access caused by a forgotten
          PIN, lost device, or browser data wiped by the user or by
          the operating system; (d) incidental or consequential
          damages, lost data, missed appointments, pet illness, or
          anything similar arising from using or being unable to use
          the service; (e) anything that follows from seeing — or not
          seeing — another family member's mood, sleep, status, note,
          tags or check-in, including across Houses in the Extended
          tier; (f) data exposed through a share link, iCal feed, or
          shared-list URL that someone in your family chose to
          generate, paste, screenshot, or forward.
        </P>
        <P>
          Where your jurisdiction does not allow the exclusion of certain
          implied warranties or the limitation of liability for
          consequential or incidental damages, the above exclusions may
          not apply to you, and our liability is limited to the minimum
          extent permitted by law.
        </P>

        <H2>Indemnity</H2>
        <P>
          You acknowledge and agree that all content you create,
          upload, or share in FamFeel — including but not limited to
          notes, custom plans, avatar uploads, calendar events, and
          messages — is your sole responsibility. You are solely
          responsible for the accuracy, legality, and appropriateness
          of such content, and for any consequences arising from it.
        </P>
        <P>
          To the fullest extent permitted by applicable law, you
          agree not to hold FamFeel liable for any claims, disputes,
          damages, or losses arising out of or related to content you
          have created, uploaded, or shared.
        </P>
        <P>
          You further agree to indemnify, defend, and hold harmless
          FamFeel, its creators, owners, operators, and affiliates
          from and against any and all claims, liabilities, damages,
          losses, and expenses (including reasonable legal fees)
          arising out of or related to (a) your content, (b) your use
          or misuse of the service, (c) any claim brought by another
          user, family member, or third party in connection with
          content you have created, uploaded, or shared, and (d) any
          actions taken or not taken in reliance on the service or
          any content, reminders, or notifications provided through
          it, whether such actions occur within or outside the app.
        </P>

        <H2>Intellectual property</H2>
        <P>
          All content, design, code, and branding within the Service
          are the property of FamFeel and its creators. The User may
          not copy, modify, or redistribute any part of the Service
          without prior written consent.
        </P>

        <H2>Force majeure</H2>
        <P>
          FamFeel shall not be liable for any failure or delay in
          performance of its obligations under these Terms to the
          extent caused by events beyond its reasonable control,
          including but not limited to internet or hosting outages,
          cyberattacks, malware, infrastructure failures, third-party
          service interruptions, governmental actions, civil unrest,
          pandemics, or natural disasters.
        </P>

        <H2>Changes</H2>
        <P>
          These terms may evolve as the app grows. Major changes will be
          announced in the app. Continued use after changes means you accept
          them.
        </P>

        <H2>Contact</H2>
        <P>
          Questions about these terms, a refund request, a data export,
          or a deletion request? Email us via the address listed on the
          FAQ page and we'll get back to you within a few business days.
        </P>
      </section>
    </MarketingLayout>
  );
}
